// src/features/schedule/components/QuickCreateModal.tsx
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { X } from 'lucide-react';
import { addMinutes, startOfDay, endOfDay } from 'date-fns';
import { TimeBoxCategory } from '../../../types/timeBox';
import { EventCategory } from '../../../types/calendar-event';
import { timeBoxCategoryConfig } from '../../../constants/timebox.constants';
import { eventCategoryConfig } from '../../../constants/event.constants';
import {
  QUICK_CREATE_TIMEBOX_CATEGORIES,
  QUICK_CREATE_EVENT_CATEGORIES,
  TIMEBOX_DURATION_OPTIONS,
  EVENT_DURATION_OPTIONS,
  QuickCreateDuration
} from '../constants/quickCreate.constants';
import { getCurrentUserId } from '../../../services/auth';
import { useTimeBoxCreate } from '../../timebox/hooks/useTimeBoxCreate';
import { createEventForCurrentUser } from '../../../services/api/calendar-events';
import { eventBus, EVENTS } from '../../../utils/eventBus';
import { logger } from '../../../utils/logger';
import { useToast } from '../../../contexts/ToastContext';
import { snapTimeToGrid } from '../../../utils/timePickerHelpers';

interface QuickCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  startTime?: Date;     // 可選的開始時間（來自時槽點擊）
  endTime?: Date;       // 可選的結束時間（供參考，實際以用戶選擇時長為準）
  initialData?: { title?: string; taskId?: string }; // 預填資料（來自 task 點擊）
  /** 超出此分鐘數的 duration 選項灰化 disabled（來自 MomentumCard 空窗上限） */
  maxDurationMinutes?: number;
  /** 在合法範圍內 highlight 此分鐘數的選項（task 本身帶有的 duration） */
  highlightDurationMinutes?: number;
}

type CreateType = 'timebox' | 'event';
type Step = 'category' | 'duration' | 'title';

const QuickCreateModal: React.FC<QuickCreateModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  startTime,
  endTime,
  initialData,
  maxDurationMinutes,
  highlightDurationMinutes,
}) => {
  const [step, setStep] = useState<Step>('category');
  const [createType, setCreateType] = useState<CreateType | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedDuration, setSelectedDuration] = useState<QuickCreateDuration | null>(null);
  const [title, setTitle] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const titleInputRef = useRef<HTMLInputElement>(null);

  const userId = getCurrentUserId();
  const { handleCreateTimeBox } = useTimeBoxCreate(userId);
  const { showError } = useToast();

  // 重置狀態
  const handleClose = useCallback(() => {
    setStep('category');
    setCreateType(null);
    setSelectedCategory(null);
    setSelectedDuration(null);
    setTitle('');
    setIsSubmitting(false);
    onClose();
  }, [onClose]);

  // 第一步：選擇類型
  const handleCategorySelect = useCallback((type: CreateType, category: string) => {
    setCreateType(type);
    setSelectedCategory(category);
    setStep('duration');
  }, []);

  // 返回上一步
  const handleBack = useCallback(() => {
    if (step === 'duration') {
      setStep('category');
      setCreateType(null);
      setSelectedCategory(null);
    } else if (step === 'title') {
      setStep('duration');
      setSelectedDuration(null);
      setTitle('');
    }
  }, [step]);

  // 第二步：選擇時長
  const handleDurationSelect = useCallback((duration: QuickCreateDuration) => {
    setSelectedDuration(duration);

    const defaultTitle = initialData?.title || getDefaultTitle(createType!, selectedCategory!);
    setTitle(defaultTitle);

    setStep('title');
  }, [createType, selectedCategory, initialData]);

  // 生成預設標題
  const getDefaultTitle = (type: CreateType, category: string): string => {
    if (type === 'timebox') {
      return timeBoxCategoryConfig[category as TimeBoxCategory].label + ' Time';
    } else {
      return eventCategoryConfig[category as EventCategory].label;
    }
  };

  // Auto focus 標題輸入框
  useEffect(() => {
    if (step === 'title' && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [step]);

  // 第三步：建立
  const handleCreate = useCallback(async () => {
    if (!createType || !selectedCategory || !selectedDuration) return;

    setIsSubmitting(true);

    try {
      // 使用傳入的時間（假設已對齊）或對齊當前時間到 15 分鐘網格
      const baseTime = startTime
        ? startTime
        : snapTimeToGrid(new Date(), 'round');
      const finalTitle = title.trim() || getDefaultTitle(createType, selectedCategory);

      if (createType === 'timebox') {
        // 建立 TimeBox
        const endTime = addMinutes(baseTime, selectedDuration as number);
        const timeBox = await handleCreateTimeBox({
          itemType: 'timebox',
          title: finalTitle,
          category: selectedCategory as TimeBoxCategory,
          startTime: baseTime,
          endTime,
          status: 'normal',
          taskIds: initialData?.taskId ? [initialData.taskId] : undefined,
        });

        logger.info('QuickCreate', 'TimeBox created successfully', {
          category: selectedCategory,
          duration: selectedDuration,
          id: timeBox.id
        });

        eventBus.publish(EVENTS.TIMEBOX.CREATED, timeBox);
      } else {
        // 建立 Event
        if (selectedDuration === 'allday') {
          // 全天事件
          const dayStart = startOfDay(baseTime);
          const dayEnd = endOfDay(baseTime);

          const eventId = await createEventForCurrentUser({
            itemType: 'event',
            title: finalTitle,
            category: selectedCategory as EventCategory,
            timeType: 'allday',
            startTime: dayStart,
            endTime: dayEnd,
            userId,
            source: 'manual'
          });

          logger.info('QuickCreate', 'All-day Event created successfully', {
            category: selectedCategory,
            id: eventId
          });
        } else {
          // 定時事件
          const endTime = addMinutes(baseTime, selectedDuration as number);

          const eventId = await createEventForCurrentUser({
            itemType: 'event',
            title: finalTitle,
            category: selectedCategory as EventCategory,
            timeType: 'timed',
            startTime: baseTime,
            endTime,
            userId,
            source: 'manual'
          });

          logger.info('QuickCreate', 'Timed Event created successfully', {
            category: selectedCategory,
            duration: selectedDuration,
            id: eventId
          });
        }

        eventBus.publish(EVENTS.EVENT.CREATED, { category: selectedCategory });
      }

      if (onSuccess) {
        onSuccess();
      }

      handleClose();
    } catch (error) {
      logger.error('QuickCreate', 'Failed to create schedule item', { error });
      showError('Failed to create', 'Please try again');
      setIsSubmitting(false);
    }
  }, [createType, selectedCategory, selectedDuration, title, startTime, initialData, handleCreateTimeBox, userId, onSuccess, handleClose, showError]);

  // Enter 鍵建立
  const handleKeyPress = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.nativeEvent.isComposing) return;
    if (e.key === 'Enter' && !isSubmitting) {
      handleCreate();
    }
  }, [handleCreate, isSubmitting]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-modal flex items-center justify-center">
      {/* 背景遮罩 */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleClose}
      />

      {/* Modal 內容 */}
      <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            {step !== 'category' && (
              <button
                onClick={handleBack}
                className="text-gray-600 hover:text-gray-900 transition-colors"
                disabled={isSubmitting}
                aria-label="Go back"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            <h2 className="text-lg font-semibold text-gray-900">
              {step === 'category' && 'Quick Create'}
              {step === 'duration' && 'Choose Duration'}
              {step === 'title' && 'Give it a name'}
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={isSubmitting}
            aria-label="Close modal"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {step === 'category' && (
            <div>
              {/* TimeBox 區塊 */}
              <div className="mb-6">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                  TIMEBOX
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  {QUICK_CREATE_TIMEBOX_CATEGORIES.map((category) => {
                    const config = timeBoxCategoryConfig[category];
                    const Icon = config.icon;

                    return (
                      <button
                        key={category}
                        onClick={() => handleCategorySelect('timebox', category)}
                        className={`
                          ${config.color}
                          p-6 rounded-xl
                          flex flex-col items-center justify-center gap-3
                          hover:scale-105 active:scale-95
                          transition-all duration-200
                          shadow-sm hover:shadow-md
                        `}
                      >
                        <Icon size={32} className="flex-shrink-0" />
                        <span className="text-base font-semibold">
                          {config.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* 分隔線 */}
              <div className="relative mb-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">or</span>
                </div>
              </div>

              {/* Event 區塊 */}
              <div>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                  EVENT
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  {QUICK_CREATE_EVENT_CATEGORIES.map((category) => {
                    const config = eventCategoryConfig[category];
                    const Icon = config.icon;

                    return (
                      <button
                        key={category}
                        onClick={() => handleCategorySelect('event', category)}
                        className="
                          bg-red-100 text-red-900
                          p-6 rounded-xl
                          flex flex-col items-center justify-center gap-3
                          hover:scale-105 active:scale-95
                          transition-all duration-200
                          shadow-sm hover:shadow-md
                        "
                      >
                        <Icon size={32} className="flex-shrink-0" />
                        <span className="text-base font-semibold">
                          {config.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {step === 'duration' && selectedCategory && (
            <div>
              {/* 顯示選中的類型 */}
              <div className="mb-6 flex items-center gap-2">
                {(() => {
                  if (createType === 'timebox') {
                    const config = timeBoxCategoryConfig[selectedCategory as TimeBoxCategory];
                    const Icon = config.icon;
                    return (
                      <>
                        <div className={`${config.color} p-2 rounded-lg`}>
                          <Icon size={20} />
                        </div>
                        <span className="text-base font-semibold text-gray-900">
                          {config.label}
                        </span>
                      </>
                    );
                  } else {
                    const config = eventCategoryConfig[selectedCategory as EventCategory];
                    const Icon = config.icon;
                    return (
                      <>
                        <div className="bg-red-100 text-red-900 p-2 rounded-lg">
                          <Icon size={20} />
                        </div>
                        <span className="text-base font-semibold text-gray-900">
                          {config.label}
                        </span>
                      </>
                    );
                  }
                })()}
              </div>

              <p className="text-sm text-gray-600 mb-4">
                How long?
              </p>

              {/* 時長選項 */}
              <div className="grid grid-cols-3 gap-3">
                {(createType === 'timebox' ? TIMEBOX_DURATION_OPTIONS : EVENT_DURATION_OPTIONS).map((duration) => {
                  if (duration === 'allday') {
                    return (
                      <button
                        key={duration}
                        onClick={() => handleDurationSelect(duration)}
                        className="
                          col-span-3
                          bg-white border-2 border-gray-300
                          hover:border-red-500 hover:bg-red-50
                          active:scale-95
                          rounded-xl p-4
                          flex items-center justify-center
                          transition-all duration-200
                          shadow-sm hover:shadow-md
                        "
                      >
                        <span className="text-lg font-bold text-gray-900">
                          All Day
                        </span>
                      </button>
                    );
                  }

                  // 超出空窗上限 → 灰化 disabled
                  const isDisabled = maxDurationMinutes !== undefined && duration > maxDurationMinutes;
                  // 在合法範圍內且符合 task duration → highlight
                  const isHighlighted = !isDisabled && highlightDurationMinutes !== undefined && duration === highlightDurationMinutes;

                  return (
                    <button
                      key={duration}
                      onClick={() => !isDisabled && handleDurationSelect(duration)}
                      disabled={isDisabled}
                      className={[
                        'border-2 rounded-xl p-4',
                        'flex flex-col items-center justify-center',
                        'transition-all duration-200',
                        isDisabled
                          ? 'bg-gray-50 border-gray-200 opacity-40 cursor-not-allowed'
                          : isHighlighted
                            ? 'bg-sky-50 border-sky-400 shadow-sm hover:border-sky-500 hover:bg-sky-100 active:scale-95'
                            : 'bg-white border-gray-300 shadow-sm hover:border-sky-500 hover:bg-sky-50 hover:shadow-md active:scale-95',
                      ].join(' ')}
                    >
                      <span className={`text-2xl font-bold ${isDisabled ? 'text-gray-400' : isHighlighted ? 'text-sky-700' : 'text-gray-900'}`}>
                        {duration}
                      </span>
                      <span className={`text-xs mt-1 ${isDisabled ? 'text-gray-300' : isHighlighted ? 'text-sky-500' : 'text-gray-600'}`}>
                        min
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step === 'title' && (
            <div>
              {/* 顯示選中的類型和時長 */}
              <div className="mb-6 flex items-center gap-2 text-sm text-gray-600">
                {(() => {
                  if (createType === 'timebox') {
                    const config = timeBoxCategoryConfig[selectedCategory as TimeBoxCategory];
                    const Icon = config.icon;
                    return (
                      <>
                        <div className={`${config.color} p-1.5 rounded`}>
                          <Icon size={16} />
                        </div>
                        <span className="font-medium">{config.label}</span>
                      </>
                    );
                  } else {
                    const config = eventCategoryConfig[selectedCategory as EventCategory];
                    const Icon = config.icon;
                    return (
                      <>
                        <div className="bg-red-100 text-red-900 p-1.5 rounded">
                          <Icon size={16} />
                        </div>
                        <span className="font-medium">{config.label}</span>
                      </>
                    );
                  }
                })()}
                <span className="text-gray-400">•</span>
                <span className="font-medium">
                  {selectedDuration === 'allday' ? 'All Day' : `${selectedDuration} min`}
                </span>
              </div>

              {/* 標題輸入 */}
              <input
                ref={titleInputRef}
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Enter title..."
                disabled={isSubmitting}
                className="
                  w-full px-4 py-3 mb-6
                  border-2 border-gray-300 rounded-xl
                  focus:border-sky-500 focus:outline-none
                  text-base text-gray-900
                  disabled:opacity-50 disabled:cursor-not-allowed
                  transition-colors
                "
              />

              {/* 建立按鈕 */}
              <button
                onClick={handleCreate}
                disabled={isSubmitting}
                className="
                  w-full py-3 px-6
                  bg-sky-600 hover:bg-sky-700
                  text-white font-semibold rounded-xl
                  disabled:opacity-50 disabled:cursor-not-allowed
                  transition-colors duration-200
                "
              >
                {isSubmitting ? 'Creating...' : 'Create'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuickCreateModal;
